import { collections, firestoreHelpers } from '@/lib/firebase';
import type { 
  DashboardStats, 
  SupportTicket, 
  AppSettings, 
  User, 
  Passenger,
  Driver, 
  Ride,
  Transaction 
} from '@/types';

export const adminService = {
  // ============ DASHBOARD STATS ============
  
  async getDashboardStats(): Promise<DashboardStats> {
    // Get counts in parallel
    const [
      passengers,
      drivers,
      rides,
      transactions,
      todayRides,
      onlineDrivers,
      pendingVerifications,
      openTickets
    ] = await Promise.all([
      firestoreHelpers.queryDocuments<Passenger>(collections.passengers, []),
      firestoreHelpers.queryDocuments<Driver>(collections.drivers, []),
      firestoreHelpers.queryDocuments<Ride>(collections.rides, []),
      firestoreHelpers.queryDocuments<Transaction>(collections.transactions, [
        { field: 'status', operator: '==', value: 'completed' }
      ]),
      this.getTodayRides(),
      firestoreHelpers.queryDocuments<Driver>(collections.drivers, [
        { field: 'isOnline', operator: '==', value: true }
      ]),
      firestoreHelpers.queryDocuments<Driver>(collections.drivers, [
        { field: 'isVerified', operator: '==', value: false }
      ]),
      firestoreHelpers.queryDocuments<SupportTicket>(collections.supportTickets, [
        { field: 'status', operator: 'in', value: ['open', 'in_progress'] }
      ]),
    ]);

    const totalRevenue = transactions.reduce((sum, t) => sum + (t.amount || 0), 0);
    const todayRevenue = todayRides.reduce((sum, r) => sum + (r.finalPrice || r.estimatedPrice || 0), 0);

    return {
      totalUsers: passengers.length,
      totalDrivers: drivers.length,
      totalRides: rides.length,
      totalRevenue,
      todayRides: todayRides.length,
      todayRevenue,
      activeRides: rides.filter(r => 
        ['searching', 'driver_assigned', 'driver_arrived', 'in_progress'].includes(r.status)
      ).length,
      onlineDrivers: onlineDrivers.length,
      pendingVerifications: pendingVerifications.length,
      openTickets: openTickets.length,
    };
  },

  async getTodayRides(): Promise<Ride[]> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [{ field: 'createdAt', operator: '>=', value: today }],
      'createdAt',
      'desc'
    );
  },

  // ============ USER MANAGEMENT ============

  async getAllUsers(): Promise<User[]> {
    const passengers = await firestoreHelpers.queryDocuments<Passenger>(collections.passengers, []);
    const drivers = await firestoreHelpers.queryDocuments<Driver>(collections.drivers, []);
    return [...passengers, ...drivers] as User[];
  },

  async searchUsers(searchTerm: string): Promise<User[]> {
    const users = await this.getAllUsers();
    return users.filter(u => 
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.phone.includes(searchTerm)
    );
  },

  async deactivateUser(userId: string, userType: 'passenger' | 'driver'): Promise<void> {
    const collection = userType === 'passenger' ? collections.passengers : collections.drivers;
    await firestoreHelpers.updateDocument(collection, userId, { isActive: false });
  },

  async reactivateUser(userId: string, userType: 'passenger' | 'driver'): Promise<void> {
    const collection = userType === 'passenger' ? collections.passengers : collections.drivers;
    await firestoreHelpers.updateDocument(collection, userId, { isActive: true });
  },

  // ============ DRIVER VERIFICATION ============

  async getPendingDrivers(): Promise<Driver[]> {
    return await firestoreHelpers.queryDocuments<Driver>(
      collections.drivers,
      [{ field: 'isVerified', operator: '==', value: false }],
      'createdAt',
      'desc'
    );
  },

  async verifyDriver(driverId: string): Promise<void> {
    await firestoreHelpers.updateDocument(collections.drivers, driverId, {
      isVerified: true,
      documentsStatus: {
        license: 'approved',
        vehicle: 'approved',
        id: 'approved',
      },
    });
  },

  async rejectDriver(driverId: string, reason: string): Promise<void> {
    await firestoreHelpers.updateDocument(collections.drivers, driverId, {
      isVerified: false,
      documentsStatus: {
        license: 'rejected',
        vehicle: 'rejected',
        id: 'rejected',
      },
      rejectionReason: reason,
    });
  },

  // ============ RIDE MANAGEMENT ============

  async getAllRides(limitCount?: number): Promise<Ride[]> {
    return await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [],
      'createdAt',
      'desc',
      limitCount
    );
  },

  async getRideDetails(rideId: string): Promise<{ ride: Ride; passenger: any; driver: any } | null> {
    const ride = await firestoreHelpers.getDocument<Ride>(collections.rides, rideId);
    if (!ride) return null;

    const [passenger, driver] = await Promise.all([
      firestoreHelpers.getDocument(collections.passengers, ride.passengerId),
      ride.driverId ? firestoreHelpers.getDocument(collections.drivers, ride.driverId) : null,
    ]);

    return { ride, passenger, driver };
  },

  async cancelRide(rideId: string, reason: string): Promise<void> {
    await firestoreHelpers.updateDocument(collections.rides, rideId, {
      status: 'cancelled',
      cancelReason: reason,
      cancelledBy: 'admin',
      cancelledAt: new Date(),
    });
  },

  // ============ FINANCE ============

  async getAllTransactions(limitCount?: number): Promise<Transaction[]> {
    return await firestoreHelpers.queryDocuments<Transaction>(
      collections.transactions,
      [],
      'createdAt',
      'desc',
      limitCount
    );
  },

  async getFinancialReport(startDate: Date, endDate: Date): Promise<{
    totalRevenue: number;
    totalRides: number;
    averageRideValue: number;
    commission: number;
  }> {
    const transactions = await firestoreHelpers.queryDocuments<Transaction>(
      collections.transactions,
      [
        { field: 'status', operator: '==', value: 'completed' },
        { field: 'createdAt', operator: '>=', value: startDate },
        { field: 'createdAt', operator: '<=', value: endDate },
      ]
    );

    const rides = await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [
        { field: 'status', operator: '==', value: 'completed' },
        { field: 'completedAt', operator: '>=', value: startDate },
        { field: 'completedAt', operator: '<=', value: endDate },
      ]
    );

    const totalRevenue = transactions.reduce((sum, t) => sum + t.amount, 0);
    const totalRides = rides.length;
    const averageRideValue = totalRides > 0 ? totalRevenue / totalRides : 0;
    const commission = totalRevenue * 0.15; // 15% commission

    return {
      totalRevenue,
      totalRides,
      averageRideValue,
      commission,
    };
  },

  // ============ SUPPORT TICKETS ============

  async createSupportTicket(ticketData: Partial<SupportTicket>): Promise<string> {
    const newTicket: Partial<SupportTicket> = {
      ...ticketData,
      status: 'open',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    return await firestoreHelpers.createDocument(collections.supportTickets, newTicket);
  },

  async getAllTickets(): Promise<SupportTicket[]> {
    return await firestoreHelpers.queryDocuments<SupportTicket>(
      collections.supportTickets,
      [],
      'createdAt',
      'desc'
    );
  },

  async getOpenTickets(): Promise<SupportTicket[]> {
    return await firestoreHelpers.queryDocuments<SupportTicket>(
      collections.supportTickets,
      [{ field: 'status', operator: 'in', value: ['open', 'in_progress'] }],
      'priority',
      'desc'
    );
  },

  async assignTicket(ticketId: string, adminId: string): Promise<void> {
    await firestoreHelpers.updateDocument(collections.supportTickets, ticketId, {
      assignedTo: adminId,
      status: 'in_progress',
      updatedAt: new Date(),
    });
  },

  async resolveTicket(ticketId: string, resolution: string): Promise<void> {
    await firestoreHelpers.updateDocument(collections.supportTickets, ticketId, {
      status: 'resolved',
      resolution,
      resolvedAt: new Date(),
      updatedAt: new Date(),
    });
  },

  async closeTicket(ticketId: string): Promise<void> {
    await firestoreHelpers.updateDocument(collections.supportTickets, ticketId, {
      status: 'closed',
      updatedAt: new Date(),
    });
  },

  // ============ APP SETTINGS ============

  async getSettings(): Promise<AppSettings | null> {
    const settings = await firestoreHelpers.queryDocuments<AppSettings>(
      collections.settings,
      []
    );
    return settings.length > 0 ? settings[0] : null;
  },

  async updateSettings(settingsId: string, data: Partial<AppSettings>): Promise<void> {
    await firestoreHelpers.updateDocument(collections.settings, settingsId, {
      ...data,
      updatedAt: new Date(),
    });
  },

  async createDefaultSettings(): Promise<string> {
    const defaultSettings: Partial<AppSettings> = {
      basePrice: 500,
      pricePerKm: 150,
      pricePerMinute: 10,
      minimumFare: 300,
      commissionRate: 15,
      cancellationFee: 100,
      freeCancellationTime: 5,
      searchRadius: 5,
      maxStops: 3,
      supportPhone: '+249123456789',
      supportEmail: 'support@rahla.com',
      socialLinks: {},
    };
    
    return await firestoreHelpers.createDocument(collections.settings, defaultSettings);
  },

  // ============ ACTIVITY LOGS ============

  async logActivity(
    action: string,
    details: any,
    userId?: string,
    userType?: string
  ): Promise<void> {
    await firestoreHelpers.createDocument(collections.activityLogs, {
      action,
      details,
      userId,
      userType,
      timestamp: new Date(),
    });
  },

  async getActivityLogs(limitCount?: number): Promise<any[]> {
    return await firestoreHelpers.queryDocuments(
      collections.activityLogs,
      [],
      'timestamp',
      'desc',
      limitCount
    );
  },

  // ============ ANALYTICS ============

  async getRidesAnalytics(days: number = 30): Promise<{
    dailyRides: { date: string; count: number; revenue: number }[];
    totalRides: number;
    totalRevenue: number;
  }> {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const rides = await firestoreHelpers.queryDocuments<Ride>(
      collections.rides,
      [
        { field: 'status', operator: '==', value: 'completed' },
        { field: 'completedAt', operator: '>=', value: startDate },
      ]
    );

    const dailyData: { [key: string]: { count: number; revenue: number } } = {};
    
    rides.forEach(ride => {
      const date = ride.completedAt?.toISOString().split('T')[0] || '';
      if (!dailyData[date]) {
        dailyData[date] = { count: 0, revenue: 0 };
      }
      dailyData[date].count++;
      dailyData[date].revenue += ride.finalPrice || ride.estimatedPrice || 0;
    });

    const dailyRides = Object.entries(dailyData)
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return {
      dailyRides,
      totalRides: rides.length,
      totalRevenue: rides.reduce((sum, r) => sum + (r.finalPrice || r.estimatedPrice || 0), 0),
    };
  }
};
